/**
 * RESPONSIBILITY: the payload shapes of the content API — query, write, read, history.
 *
 * Two endpoints carry the whole CMS: `POST /api/sections/query` and `PUT /api/sections`.
 * These are what they send and what they answer.
 */
import type { Lang } from '../lang';
import type { SectionStatus, RevType, ApiError } from './status';

/**
 * How long a client's copy on disk stays usable — the offline window, not the freshness
 * window. A browser opened after this starts from nothing rather than from a stale page.
 */
export const CACHE_TTL_MS = 30 * 60 * 1000;

/**
 * How long a client may show a section WITHOUT asking whether it changed.
 *
 * Zero, and deliberately zero. Revalidating is one batched request for the whole page
 * that answers CURRENT with no payload when nothing moved, so the cost of asking is a
 * few hundred bytes — while the cost of not asking was an editor saving a correction
 * and every other browser, their own included after a reload, showing the old words for
 * up to half an hour with nothing to explain it and no way to force it.
 *
 * The cached copy is still what renders the moment a page mounts; this only decides
 * whether a question goes out behind it.
 */
export const CONTENT_STALE_MS = 0;

/** Revisions the history endpoint returns, newest first. Every one carries its whole document. */
export const HISTORY_LIMIT = 50;

/* ---- QUERY --------------------------------------------------------------- */

export interface SectionQueryEntry {
  name: string;
  version?: number;
}

export interface SectionQueryRequest {
  lang: Lang;
  objects: SectionQueryEntry[];
}

export interface SectionQueryResult {
  name: string;
  status: SectionStatus;
  version?: number;
  canWrite?: boolean;
  lang?: Lang;
  type?: string;
  data?: Record<string, unknown>;
  updatedBy?: string;
  updatedAt?: number;
}

export interface SectionQueryResponse {
  results: SectionQueryResult[];
}

/* ---- PUT ----------------------------------------------------------------- */

export interface SectionWriteRequest {
  name: string;
  lang: Lang;
  version?: number;          // required for UPDATE and DELETE
  data: unknown;
  comment?: string;
}

export interface SectionWriteResponse {
  name: string;
  lang: Lang;
  version: number;
  updatedAt: number;
  /**
   * What was STORED, which is not what was sent: @Derived fields are refilled and
   * markdown is rendered server-side. Returned so the editor's own browser can hold the
   * real document — caching the submitted draft left it showing the previous render of
   * whatever it had just changed.
   */
  data: Record<string, unknown>;
}

export interface ValidationIssue { path: (string | number)[]; message: string }

export interface VersionConflictBody {
  error: ApiError.VersionConflict;
  yourVersion?: number;
  currentVersion?: number;
  current: { data: Record<string, unknown>; updatedBy: string; updatedAt: number } | null;
}

/* ---- single section + history -------------------------------------------- */

export interface SectionRead {
  name: string;
  lang: Lang;
  version: number;
  data: Record<string, unknown>;
  exists: boolean;
  canWrite: boolean;
}

export interface Revision {
  rev: number;
  revType: RevType;
  version: number;
  updatedAt: number;
  changedBy: string;
  comment: string | null;
  data: Record<string, unknown> | null;
}

export interface SectionHistory {
  lang: Lang;
  currentVersion: number;
  langs: readonly Lang[];
  revisions: Revision[];
}
